import { useState, useEffect } from 'react'
import { Row, Col, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from '../../../services/api'
import { Path, GasPump, Truck, Scales, Clock } from 'phosphor-react'
import { errorApi } from '../../../utils/errorApi'

import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryTheme,
  VictoryTooltip,
  VictoryZoomContainer,
} from 'victory'

function VehiclePerformance({ vehicleId, date = '' }) {
  const [performanceInfo, setPerformanceInfo] = useState({})
  const [deliveryTimesChartData, setDeliveryTimesChartData] = useState({})
  const [xZoomDomain, setXZoomDomain] = useState([0, 15])

  const navigate = useNavigate()

  const { setIsLoading } = useLoading()

  const minValue = 1.01

  const handleDomainChange = (domain) => {
    setXZoomDomain(domain.x)
  }

  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true)
        let performanceData = await api.get(
          `/company/vehicles/${vehicleId}/performance?date=${date}`,
        )
        const deliveryTimesData = performanceData.data.delivery_times_minutes
        let deliveryTimesChartData = deliveryTimesData.map((time, index) => ({
          x: (index + 1).toFixed(0),
          y: time === 0 ? 0 : Math.log10(time + minValue),
        }))

        setDeliveryTimesChartData(deliveryTimesChartData)
        setPerformanceInfo(performanceData.data)
      } catch (err) {
        toast.error(
          errorApi(err, 'Falha ao buscar as estatísticas do veículo. Tente novamente mais tarde.'),
        )
      }
      setIsLoading(false)
    }
    loadData()
  }, [vehicleId, date, setIsLoading])

  return (
    <div>
      <Row>
        <Col className={`${styles.infoContainer} col-12 col-md-5 border`}>
          <Row>
            <Col className="col-4 text-center">
              <GasPump className={styles.infoIcon} color="#053C5E" size={50} weight="light" />{' '}
            </Col>
            <Col className="col-8 text-center d-flex flex-column align-items-center justify-content-center">
              <span style={{ fontSize: 12 }}>Combustível abastecido</span>
              <span className={styles.infoData}>
                {performanceInfo.fuel_consumption + ' litros'}
              </span>
            </Col>
          </Row>
        </Col>
        <Col className={`${styles.infoContainer} col-12 col-md-5 offset-md-1 border`}>
          <Row>
            <Col className="col-4 text-center">
              <Path className={styles.infoIcon} color="#053C5E" size={50} weight="light" />{' '}
            </Col>
            <Col className="col-8 text-center d-flex flex-column align-items-center justify-content-center">
              <span style={{ fontSize: 12 }}>Deslocamento</span>
              <span className={styles.infoData}>
                {performanceInfo.distance_traveled_kilometers} Km
              </span>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col className={`${styles.infoContainer} col-12 col-md-11 border`}>
          <Row className="mb-0">
            <Col className="col-2 text-center">
              <Clock className={styles.infoIcon} color="#053C5E" size={50} weight="light" />{' '}
            </Col>
            <Col className="col-10 col-md-8 text-center d-flex flex-column align-items-center align-items-md-start justify-content-center">
              <span style={{ fontSize: 12 }}>Tempo por</span>
              <span className={styles.infoData}>entrega realizada (min)</span>
            </Col>
          </Row>

          {deliveryTimesChartData.length > 1 ? (
            <VictoryChart
              domainPadding={{ y: 20, x: 0 }}
              domain={{ x: [0, deliveryTimesChartData.length + 1] }}
              theme={VictoryTheme.clean}
              height={200}
              padding={{ top: -20, bottom: 100, left: 50, right: 0 }}
              containerComponent={
                <VictoryZoomContainer
                  allowZoom={false}
                  zoomDimension="x"
                  allowPan={deliveryTimesChartData.length >= xZoomDomain[1] - 1}
                  zoomDomain={{ x: xZoomDomain }}
                  onZoomDomainChange={(domain) => handleDomainChange(domain)}
                />
              }
            >
              <VictoryAxis crossAxis label="Nº Entrega" />
              <VictoryAxis
                dependentAxis
                crossAxis
                tickFormat={(tick) => (tick === 0 ? 0 : (Math.pow(10, tick) - minValue).toFixed(0))}
              />
              <VictoryBar
                barWidth={20}
                cornerRadius={5}
                data={deliveryTimesChartData}
                labelComponent={
                  <VictoryTooltip
                    cornerRadius={8}
                    dy={-5}
                    style={{
                      fill: '#FFFFFF',
                    }}
                    flyoutStyle={{
                      fill: '#053C5E',
                    }}
                  />
                }
                labels={({ datum }) =>
                  `Tempo (min): ${datum.y === 0 ? 0 : (Math.pow(10, datum.y) - minValue).toFixed(2)}`
                }
                style={{
                  data: {
                    fill: '#c3c3c3',
                    fillOpacity: 1,
                    stroke: '#053C5E',
                    strokeWidth: 1,
                  },
                }}
              />
            </VictoryChart>
          ) : (
            <div
              className="text-center mb-2"
              style={{
                backgroundColor: '#053C5E',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                fontWeight: '500',
                witdh: '100%',
              }}
            >
              Nenhuma entrega realizada
            </div>
          )}
        </Col>
      </Row>

      <Row>
        <Col className={`${styles.infoContainer} col-12 col-md-5 border`}>
          <Row>
            <Col className="col-4 text-center">
              <Truck className={styles.infoIcon} color="#053C5E" size={50} weight="light" />{' '}
            </Col>
            <Col className="col-8 text-center d-flex flex-column align-items-center justify-content-center">
              <span style={{ fontSize: 12 }}>Entregas realizadas</span>
              <span className={styles.infoData}>{performanceInfo.requests_completed}</span>
            </Col>
          </Row>
        </Col>
        <Col className={`${styles.infoContainer} col-12 col-md-5 offset-md-1 border`}>
          <Row>
            <Col className="col-4 text-center">
              <Scales className={styles.infoIcon} color="#053C5E" size={50} weight="light" />{' '}
            </Col>
            <Col className="col-8 text-center d-flex flex-column align-items-center justify-content-center">
              <span style={{ fontSize: 12 }}>Peso transportado</span>
              <span className={styles.infoData}>{performanceInfo.weight_transported + ' Kg'}</span>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col
          md={11}
          style={{
            marginBottom: 15,
            marginTop: 20,
            borderRadius: 8,
          }}
        >
          <Row>
            <Col>
              <Button
                className={styles.dailyDataBtn}
                onClick={() => navigate(`/empresa/veiculos/info/${vehicleId}/entregas-do-dia`)}
              >
                Ver dados das entregas do dia
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  )
}

export default VehiclePerformance
